import { createContext, useContext, useReducer, useEffect } from 'react'
import ErrorPage from '../components/UniversalComponents/ErrorPage'
import Home from '../components/Home/index'

import {
    Boats,
    Users,
    Engines,
    Devices,
    Dealers,
    Signals,
} from '../components/Admin'
import Support from '../components/Support'
import { FleetAnalytics } from '../components/Analytics'
import {
    createBrowserRouter,
    createRoutesFromElements,
    Route,
    RouterProvider,
    Navigate,
} from 'react-router-dom'
import NavSidebarRoot from '../components/Navigation'
import {
    Hub as HubIcon,
    Group as GroupIcon,
    DeveloperBoard,
    AutoGraph as AutoGraphIcon,
    SupportAgent,
    BookOnline,
} from '@mui/icons-material'

import { useAuth } from './AuthenticationContext'
import DeactivateAccount from '../components/LoginPage/DeactivateAccount'
import VerifyEmail from '../components/LoginPage/VerifyEmail'
import PatchNotes from '../components/Developer/PatchNotes'
import SystemMonitor from '../components/Developer/SystemMonitor'
import Manufacturer from '../components/Admin/Manufacturers'
import Dashboard from '../components/Analytics/Dashboard'
import { useGlobalState } from './GlobalStateContext'

const defaultNavMenuLinks = [
    {
        path: '/',
        element: <Home />,
        name: 'Fleet',
        errorElement: <ErrorPage />,
        icon: <HubIcon />,
    },
    {
        path: 'users',
        name: 'Admin',
        errorElement: <ErrorPage />,
        icon: <GroupIcon />,
        superAdmin: false,
        children: [
            {
                path: 'admin/boats',
                element: <Boats />,
                name: 'Boats',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/dealers',
                element: <Dealers />,
                name: 'Dealers',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/devices',
                element: <Devices />,
                name: 'Devices',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/engines',
                element: <Engines />,
                name: 'Engines',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/manufacturer',
                element: <Manufacturer restoredData={''} />,
                name: 'Manufacturer',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/signals',
                element: <Signals />,
                name: 'Signals',
                errorElement: <ErrorPage />,
            },
            {
                path: 'admin/users',
                element: <Users />,
                name: 'Users',
                errorElement: <ErrorPage />,
            },
            // {
            //     path: 'admin/boatregistration',
            //     element: <BoatRegistrationForm />,
            //     name: 'Boat Registration Form',
            //     errorElement: <ErrorPage />,
            // },
        ],
    },

    {
        path: 'Analytics',
        element: <Home />,
        name: 'Analytics',
        errorElement: <ErrorPage />,
        superAdmin: false,
        icon: <AutoGraphIcon />,
        children: [
            // {
            //     path: 'data-hub/tag-manager',
            //     element: <LiveSensors />,
            //     name: 'Tag Manager',
            //     errorElement: <ErrorPage />,
            // },
            {
                path: 'analytics/fleet-analytics',
                element: <FleetAnalytics />,
                name: 'Fleet Analytics',
                errorElement: <ErrorPage />,
            },
            {
                path: 'data-hub/dashboard',
                element: <Dashboard />,
                name: 'Dashboard',
                errorElement: <ErrorPage />,
            },
        ],
    },
    {
        path: 'System',
        element: <Home />,
        name: 'System',
        errorElement: <ErrorPage />,
        icon: <DeveloperBoard />,
        superAdmin: true,
        children: [
            {
                path: 'system/patch-notes',
                element: <PatchNotes />,
                name: 'Patch Notes',
                errorElement: <ErrorPage />,
            },
            {
                path: 'system/system-monitoring',
                element: <SystemMonitor />,
                name: 'System Monitoring',
                errorElement: <ErrorPage />,
            },
        ],
    },
    {
        path: 'Support',
        element: <Support />,
        name: 'Support',
        errorElement: <ErrorPage />,
        icon: <SupportAgent />,
        superAdmin: true,
    },
]

export const NavMenu = createContext({ navMenuLinks: defaultNavMenuLinks })

export const NavMenuContext = () => {
    const defaultState = {
        navMenuLinks: defaultNavMenuLinks,
    }
    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const { navMenuLinks } = state

    const currentUser = useAuth()
    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const auth = currentUser.auth
    const { authenticated } = auth
    const { userProfile } = oupUser
    const { superAdmin, userProfilePermissions } = userProfile

    // Checking if the user is a Super Admin
    const isSuperAdmin = superAdmin === true

    // checking userProfilePermissions from Auth
    const permissions = Object.keys(userProfilePermissions || {}).filter(
        (permission) => userProfilePermissions[permission].read
    )

    /// Filter nav on admin
    const adminPermissions = navMenuLinks[1].children?.filter((child) =>
        permissions.includes(child.name)
    )
    const clonedAdmin = { ...navMenuLinks[1], children: adminPermissions }

    ///Filter nave on data hub
    const dataHubPermissions = navMenuLinks[2].children?.filter((child) =>
        permissions.includes(child.name)
    )
    const clonedDataHub = { ...navMenuLinks[2], children: dataHubPermissions }
    const newNavMenu: any = isSuperAdmin
        ? defaultNavMenuLinks
        : [navMenuLinks[0], clonedAdmin, clonedDataHub]

    const links: JSX.Element[] = []

    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route
                path="/"
                errorElement={<ErrorPage />}
                element={<NavSidebarRoot />}
            >
                {navMenuLinks?.map(
                    ({ path, element, name, children, superAdmin }) => {
                        if (children && children.length !== 0) {
                            children.map((child) =>
                                links.push(
                                    <Route
                                        path={child.path}
                                        key={child.name}
                                        element={
                                            isSuperAdmin && superAdmin ? (
                                                child.element
                                            ) : permissions.includes(
                                                  child.name
                                              ) || isSuperAdmin ? (
                                                child.element
                                            ) : (
                                                <Navigate to="/error" />
                                            )
                                        }
                                    />
                                )
                            )
                        } else {
                            links.push(
                                <Route
                                    path={path}
                                    key={name}
                                    element={element}
                                />
                            )
                        }
                        links.push(
                            <Route
                                path="deactivate-account"
                                key="deactivate-account-key"
                                element={<DeactivateAccount />}
                            />
                        )
                        links.push(
                            <Route
                                path="/verify-user/:id"
                                key="verify-user-email-by-id"
                                element={<VerifyEmail />}
                            />
                        )
                        return links
                    }
                )}
            </Route>
        )
    )

    useEffect(() => {
        if (authenticated) {
            setState({ navMenuLinks: newNavMenu })
        }
    }, [authenticated])

    return (
        <NavMenu.Provider value={{ navMenuLinks: newNavMenu }}>
            <RouterProvider router={router} />
        </NavMenu.Provider>
    )
}

export const useNavMenu = () => useContext(NavMenu)
